import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import {
  PiggyBank, Landmark, Mail, Coins, LineChart, ShieldCheck,
  AlertTriangle, ExternalLink, Calculator, ArrowUpDown, Sparkles,
  Info, ChevronRight, TrendingUp, Award, Users, Wheat, Baby, Crown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'fd',       label: 'Fixed Deposits',  icon: PiggyBank,  color: 'emerald' },
  { key: 'bonds',    label: 'Govt Bonds',      icon: Landmark,   color: 'sky' },
  { key: 'po',       label: 'Post Office',     icon: Mail,       color: 'amber' },
  { key: 'gold',     label: 'Gold',            icon: Coins,      color: 'yellow' },
  { key: 'mf',       label: 'Mutual Funds',    icon: LineChart,  color: 'indigo' },
  { key: 'retire',   label: 'Retirement',      icon: ShieldCheck,color: 'rose' },
];

// ────────────────────────────────────────────────────────────────────────────
// Personalisation rules
// ────────────────────────────────────────────────────────────────────────────
function buildPersonalisation(user) {
  if (!user) return null;
  const tips = [];
  const age = parseInt(user.age) || 0;
  const occ = (user.occupation || '').toLowerCase();
  const gender = (user.gender || '').toLowerCase();

  if (age >= 60) {
    tips.push({
      icon: Crown,
      title: 'Senior Citizen — best for you',
      items: [
        { label: 'Senior Citizen Savings Scheme (SCSS)', why: '8.2% guaranteed by Govt of India, ₹30 lakh limit, 5-yr lock-in', target: 'po' },
        { label: 'RBI Floating Rate Savings Bond', why: '8.05% p.a., reset every 6 months, fully Govt-backed', target: 'bonds' },
        { label: 'Senior-citizen FD rates', why: '+0.50% bonus on bank FDs', target: 'fd' },
      ],
    });
  }

  if (occ.includes('agriculture') || occ.includes('farm') || occ.includes('manual labor') || occ.includes('handicraft')) {
    tips.push({
      icon: Wheat,
      title: 'Farmer / Rural worker — best for you',
      items: [
        { label: 'Kisan Vikas Patra (KVP)', why: 'Money doubles in ~115 months. Available at every Post Office.', target: 'po' },
        { label: 'Post Office Time Deposit', why: 'No bank-branch needed. 6.9–7.5% p.a.', target: 'po' },
        { label: 'PPF — long-term tax-free corpus', why: '7.1% tax-free, even ₹500/year keeps the account live', target: 'po' },
      ],
    });
  }

  if (gender === 'female' || (user.has_girl_child) || occ.includes('home maker')) {
    tips.push({
      icon: Baby,
      title: 'For Women / Girl-child savings',
      items: [
        { label: 'Sukanya Samriddhi Yojana (SSY)', why: '8.2% tax-free for a girl child under 10', target: 'po' },
        { label: 'Mahila Samman Savings Certificate', why: '7.5% p.a., 2-year tenure, women only, max ₹2 lakh', target: 'po' },
      ],
    });
  }

  if (age > 0 && age < 30) {
    tips.push({
      icon: TrendingUp,
      title: 'Young investor — start early',
      items: [
        { label: 'NPS Tier I', why: 'Extra ₹50,000 deduction under 80CCD(1B). Equity allocation possible.', target: 'retire' },
        { label: 'Sovereign Gold Bond', why: '2.5% interest + gold price appreciation, no storage cost', target: 'gold' },
        { label: 'Mutual Fund SIPs', why: 'Start with ₹500/month on Groww, Coin or Paytm Money', target: 'mf' },
      ],
    });
  }

  if (occ.includes('it') || occ.includes('government') || occ.includes('bank') || occ.includes('teacher')) {
    tips.push({
      icon: Award,
      title: 'Salaried — tax-saver picks',
      items: [
        { label: 'PPF — ₹1.5 lakh under 80C', why: 'Fully tax-free (EEE) — corpus + interest + maturity', target: 'po' },
        { label: 'NPS — extra ₹50k under 80CCD(1B)', why: 'Beyond your standard 80C cap', target: 'retire' },
        { label: '5-yr tax-saver FD', why: 'Qualifies under 80C deduction', target: 'fd' },
      ],
    });
  }

  return tips.length ? tips : null;
}

// ────────────────────────────────────────────────────────────────────────────
// FD Calculator (pure browser math)
// ────────────────────────────────────────────────────────────────────────────
function FDCalculator() {
  const [principal, setPrincipal] = useState(100000);
  const [years, setYears] = useState(3);
  const [rate, setRate] = useState(7.0);
  const [mode, setMode] = useState('quarterly'); // simple | quarterly

  const result = useMemo(() => {
    const P = Number(principal) || 0;
    const r = (Number(rate) || 0) / 100;
    const t = Number(years) || 0;
    let maturity;
    if (mode === 'simple') {
      maturity = P + P * r * t;
    } else {
      // quarterly compounding
      maturity = P * Math.pow(1 + r / 4, 4 * t);
    }
    const interest = maturity - P;
    return {
      maturity: Math.round(maturity),
      interest: Math.round(interest),
    };
  }, [principal, years, rate, mode]);

  const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

  return (
    <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-6" data-testid="fd-calculator">
      <div className="flex items-center gap-2 mb-5">
        <Calculator className="w-5 h-5 text-emerald-700" />
        <h3 className="text-lg font-semibold text-gray-900">FD Calculator</h3>
        <span className="text-xs text-gray-500 ml-2">Browser-only · No data sent to server</span>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="space-y-5">
          {/* Principal */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="text-sm font-medium text-gray-700">Principal amount</label>
              <span className="text-sm font-semibold text-emerald-700">{fmt(principal)}</span>
            </div>
            <input
              type="range"
              min="1000"
              max="10000000"
              step="1000"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              className="w-full accent-emerald-600"
              data-testid="fd-principal-slider"
            />
            <input
              type="number"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="fd-principal-input"
            />
          </div>

          {/* Tenure */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="text-sm font-medium text-gray-700">Tenure (years)</label>
              <span className="text-sm font-semibold text-emerald-700">{years} {years === 1 ? 'year' : 'years'}</span>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              step="1"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              className="w-full accent-emerald-600"
              data-testid="fd-years-slider"
            />
          </div>

          {/* Rate */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="text-sm font-medium text-gray-700">Interest rate (% p.a.)</label>
              <span className="text-sm font-semibold text-emerald-700">{Number(rate).toFixed(2)}%</span>
            </div>
            <input
              type="range"
              min="3"
              max="9"
              step="0.05"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="w-full accent-emerald-600"
              data-testid="fd-rate-slider"
            />
          </div>

          {/* Mode */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Compounding</label>
            <div className="inline-flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setMode('simple')}
                className={`px-3 py-1.5 text-sm rounded-md transition ${mode === 'simple' ? 'bg-white shadow text-emerald-700 font-medium' : 'text-gray-600'}`}
                data-testid="fd-mode-simple"
              >
                Simple interest
              </button>
              <button
                onClick={() => setMode('quarterly')}
                className={`px-3 py-1.5 text-sm rounded-md transition ${mode === 'quarterly' ? 'bg-white shadow text-emerald-700 font-medium' : 'text-gray-600'}`}
                data-testid="fd-mode-quarterly"
              >
                Quarterly compounding
              </button>
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-100 rounded-xl p-6 flex flex-col justify-center">
          <p className="text-xs uppercase tracking-wide text-emerald-700 font-medium">Maturity value</p>
          <p className="text-3xl md:text-4xl font-bold text-emerald-900 mt-1" data-testid="fd-maturity-value">{fmt(result.maturity)}</p>
          <div className="mt-4 pt-4 border-t border-emerald-200 grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-600">Principal</p>
              <p className="text-base font-semibold text-gray-900">{fmt(principal)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Interest earned</p>
              <p className="text-base font-semibold text-emerald-700" data-testid="fd-interest-earned">{fmt(result.interest)}</p>
            </div>
          </div>
          <p className="mt-4 text-xs text-gray-500 leading-relaxed">
            Indicative only. Actual maturity depends on the bank's TDS, compounding frequency, and any premature withdrawal.
          </p>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section 1 — FD Rates
// ────────────────────────────────────────────────────────────────────────────
function FDSection({ rows }) {
  const [type, setType] = useState('all');
  const [sortKey, setSortKey] = useState('rate_3yr');
  const [senior, setSenior] = useState(false);

  const filtered = useMemo(() => {
    let r = rows.slice();
    if (type !== 'all') r = r.filter((x) => x.bank_type === type);
    r.sort((a, b) => {
      const aRate = (a[sortKey] || 0) + (senior ? (a.senior_citizen_rate || 0) : 0);
      const bRate = (b[sortKey] || 0) + (senior ? (b.senior_citizen_rate || 0) : 0);
      return bRate - aRate;
    });
    return r;
  }, [rows, type, sortKey, senior]);

  const types = [
    { key: 'all',         label: 'All' },
    { key: 'government',  label: 'Govt Banks' },
    { key: 'private',     label: 'Private' },
    { key: 'sfb',         label: 'Small Finance' },
    { key: 'nbfc',        label: 'NBFCs' },
  ];

  return (
    <div className="space-y-6">
      <FDCalculator />

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Bank FD Rates</h3>
            <p className="text-xs text-gray-500">Rates published by issuers. Updated periodically.</p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid="fd-senior-toggle">
            <input type="checkbox" checked={senior} onChange={(e) => setSenior(e.target.checked)} className="accent-emerald-600" />
            Include senior-citizen bonus
          </label>
        </div>

        {/* Filter chips */}
        <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap gap-2">
          {types.map((t) => (
            <button
              key={t.key}
              onClick={() => setType(t.key)}
              data-testid={`fd-filter-${t.key}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition ${
                type === t.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="ml-auto inline-flex items-center gap-2">
            <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              className="text-xs border border-gray-200 rounded-md px-2 py-1"
              data-testid="fd-sort-select"
            >
              <option value="rate_1yr">Sort by 1-yr</option>
              <option value="rate_2yr">Sort by 2-yr</option>
              <option value="rate_3yr">Sort by 3-yr</option>
              <option value="rate_5yr">Sort by 5-yr</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fd-rates-table">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Bank</th>
                <th className="px-3 py-3 text-right">1 yr</th>
                <th className="px-3 py-3 text-right">2 yr</th>
                <th className="px-3 py-3 text-right">3 yr</th>
                <th className="px-3 py-3 text-right">5 yr</th>
                <th className="px-3 py-3 text-right">Min</th>
                <th className="px-3 py-3 text-center">DICGC</th>
                <th className="px-3 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.bank_name} className="border-t border-gray-100 hover:bg-emerald-50/30">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{b.bank_name}</div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500">{b.bank_type}</div>
                  </td>
                  {['rate_1yr', 'rate_2yr', 'rate_3yr', 'rate_5yr'].map((k) => (
                    <td key={k} className="px-3 py-3 text-right tabular-nums">
                      <span className="text-gray-900 font-medium">{Number(b[k]).toFixed(2)}%</span>
                      {senior && b.senior_citizen_rate > 0 && (
                        <span className="block text-[10px] text-emerald-600">+{b.senior_citizen_rate.toFixed(2)}%</span>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-3 text-right text-xs text-gray-600">₹{(b.minimum_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-center">
                    {b.dicgc_insured ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                        <ShieldCheck className="w-3 h-3" /> Insured
                      </span>
                    ) : (
                      <span className="text-[10px] text-amber-700">Not insured</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <a
                      href={b.official_website_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline"
                      data-testid={`fd-link-${b.bank_name.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-900 leading-relaxed">
          <strong>Note:</strong> NBFC deposits are <em>not</em> DICGC-insured. Bank deposits are insured up to ₹5 lakh per depositor by DICGC. Senior-citizen bonus shown is over the base rate.
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section 2 — Government Bonds
// ────────────────────────────────────────────────────────────────────────────
function BondsSection({ rows }) {
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="bonds-grid">
      {rows.map((b) => (
        <div key={b.name} className="bg-white rounded-2xl border border-sky-100 shadow-sm p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-base font-semibold text-gray-900">{b.name}</h4>
              <p className="text-xs text-gray-500 mt-0.5">{b.issuer}</p>
            </div>
            <span className="inline-flex items-center gap-1 text-xs text-sky-700 bg-sky-50 px-2 py-1 rounded-full">
              <Landmark className="w-3 h-3" /> Govt-backed
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-[10px] uppercase text-gray-400">Rate</p>
              <p className="text-gray-900 font-medium">{b.rate}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-400">Tenure</p>
              <p className="text-gray-900 font-medium">{b.tenure}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-400">Min amount</p>
              <p className="text-gray-900">{b.min_amount}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-400">Max</p>
              <p className="text-gray-900">{b.max_amount}</p>
            </div>
          </div>

          <div className="mt-3 text-sm">
            <p className="text-[10px] uppercase text-gray-400 mb-0.5">How to buy</p>
            <p className="text-gray-700 leading-relaxed">{b.how_to_buy}</p>
          </div>

          <div className="mt-3 text-sm">
            <p className="text-[10px] uppercase text-gray-400 mb-0.5">Tax</p>
            <p className="text-gray-700 leading-relaxed">{b.tax_benefit}</p>
          </div>

          {b.links?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {b.links.map((l, j) => (
                <a
                  key={j}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline bg-sky-50 px-2.5 py-1.5 rounded-md"
                >
                  {l.label} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section 3 — Post Office Schemes
// ────────────────────────────────────────────────────────────────────────────
function PostOfficeSection({ rows }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="post-office-grid">
      {rows.map((p) => (
        <div key={p.name} className="bg-white rounded-2xl border border-amber-100 shadow-sm p-5 hover:shadow-md transition">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <Mail className="w-4 h-4 text-amber-700" />
            </div>
            <h4 className="text-sm font-semibold text-gray-900 leading-tight">{p.name}</h4>
          </div>

          <div className="text-2xl font-bold text-amber-700 mb-3">{p.rate}</div>

          <div className="space-y-1.5 text-xs text-gray-700">
            <p><strong className="text-gray-500">Min:</strong> {p.min_amount}</p>
            <p><strong className="text-gray-500">Max:</strong> {p.max_amount}</p>
            <p><strong className="text-gray-500">Lock-in:</strong> {p.lock_in}</p>
            <p><strong className="text-gray-500">Tax:</strong> {p.tax_benefit}</p>
            <p><strong className="text-gray-500">Eligibility:</strong> {p.who_can_invest}</p>
          </div>

          <a
            href={p.official_link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:underline"
            data-testid={`po-link-${p.name.replace(/\s+/g, '-').toLowerCase().slice(0, 30)}`}
          >
            India Post page <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section 4 — Gold Options
// ────────────────────────────────────────────────────────────────────────────
function GoldSection({ rows }) {
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="gold-grid">
      {rows.map((g) => (
        <div key={g.name} className="bg-white rounded-2xl border border-yellow-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <Coins className="w-5 h-5 text-yellow-600" />
            <h4 className="text-base font-semibold text-gray-900">{g.name}</h4>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-emerald-700 font-medium mb-1">Pros</p>
              <ul className="space-y-1 text-gray-700">
                {g.pros.map((p, j) => (
                  <li key={j} className="flex gap-1.5"><span className="text-emerald-500">+</span><span>{p}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-rose-700 font-medium mb-1">Cons</p>
              <ul className="space-y-1 text-gray-700">
                {g.cons.map((c, j) => (
                  <li key={j} className="flex gap-1.5"><span className="text-rose-500">−</span><span>{c}</span></li>
                ))}
              </ul>
            </div>
          </div>

          {g.links?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {g.links.map((l, j) => (
                <a
                  key={j}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-yellow-800 bg-yellow-50 hover:bg-yellow-100 px-2.5 py-1.5 rounded-md"
                >
                  {l.label} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section 5 — Mutual Fund platforms
// ────────────────────────────────────────────────────────────────────────────
function MFSection({ rows }) {
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="mf-grid">
      {rows.map((m) => (
        <div key={m.name} className="bg-white rounded-2xl border border-indigo-100 shadow-sm p-5 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <LineChart className="w-5 h-5 text-indigo-600" />
            <h4 className="text-base font-semibold text-gray-900">{m.name}</h4>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed mb-3">{m.tagline}</p>

          <ul className="text-xs text-gray-700 space-y-1 mb-4">
            {m.features?.map((f, j) => (
              <li key={j} className="flex gap-1.5"><span className="text-indigo-500">•</span>{f}</li>
            ))}
          </ul>

          <a
            href={m.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto inline-flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-2 rounded-lg"
            data-testid={`mf-link-${m.name.replace(/\s+/g, '-').toLowerCase()}`}
          >
            Visit {m.name} <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      ))}
      <div className="md:col-span-3 bg-indigo-50 border border-indigo-100 text-indigo-900 text-xs p-3 rounded-lg leading-relaxed">
        <strong>Note:</strong> Mutual fund investments are subject to market risks. Please read all scheme-related documents carefully before investing. KnowMySchemes does not promote or recommend any specific platform.
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section 6 — Retirement
// ────────────────────────────────────────────────────────────────────────────
function RetirementSection({ rows }) {
  return (
    <div className="space-y-5" data-testid="retirement-grid">
      {rows.map((r) => (
        <div key={r.name} className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{r.name}</h4>
              <p className="text-sm text-rose-700 font-medium mt-1">{r.rate}</p>
            </div>
          </div>

          <p className="text-sm text-gray-700 leading-relaxed mt-3">{r.summary}</p>

          {r.tax_benefit && (
            <p className="text-sm text-gray-700 leading-relaxed mt-2">
              <strong className="text-gray-900">Tax benefit:</strong> {r.tax_benefit}
            </p>
          )}

          {r.how_to_check_balance && (
            <p className="text-sm text-gray-700 leading-relaxed mt-2">
              <strong className="text-gray-900">Check balance:</strong> {r.how_to_check_balance}
            </p>
          )}

          {r.contribution_examples?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <p className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-2">Monthly contribution examples</p>
              <table className="w-full text-xs border border-gray-100 rounded-lg overflow-hidden">
                <thead className="bg-rose-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Age at entry</th>
                    <th className="px-3 py-2 text-left">Pension target</th>
                    <th className="px-3 py-2 text-right">Monthly contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {r.contribution_examples.map((c, j) => (
                    <tr key={j} className="border-t border-gray-100">
                      <td className="px-3 py-2">{c.age_at_entry}</td>
                      <td className="px-3 py-2">₹{c.pension_target.toLocaleString('en-IN')}/month</td>
                      <td className="px-3 py-2 text-right font-medium">₹{c.monthly_contribution.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {r.links?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {r.links.map((l, j) => (
                <a
                  key={j}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-rose-700 hover:underline bg-rose-50 px-2.5 py-1.5 rounded-md"
                >
                  {l.label} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Personalisation banner
// ────────────────────────────────────────────────────────────────────────────
function PersonalisationBanner({ tips, onJump }) {
  if (!tips || !tips.length) return null;
  return (
    <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-amber-50 border border-emerald-200 rounded-2xl p-6 mb-8" data-testid="personalisation-banner">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-emerald-700" />
        <h2 className="text-base font-semibold text-emerald-900">Picked for your profile</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {tips.map((t) => {
          const Icon = t.icon || Users;
          return (
            <div key={t.title} className="bg-white/80 backdrop-blur rounded-xl border border-emerald-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-emerald-700" />
                <h3 className="text-sm font-semibold text-gray-900">{t.title}</h3>
              </div>
              <ul className="space-y-1.5">
                {t.items.map((it, j) => (
                  <li key={j}>
                    <button
                      onClick={() => onJump(it.target)}
                      className="text-left text-xs text-gray-700 hover:text-emerald-700 inline-flex items-start gap-1.5 leading-relaxed"
                    >
                      <ChevronRight className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" />
                      <span><span className="font-medium text-gray-900">{it.label}</span> — {it.why}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────
const MoneyHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('fd');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = 'KMS Money Hub — Compare FD Rates, Bonds, Post Office & Gold Investments | KnowMySchemes';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = 'Compare bank FD rates, government bonds, post office savings (PPF/SCSS/SSY), gold investment options, mutual funds & retirement plans. Information-only platform — no transactions.';
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/money-hub/all`);
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to load Money Hub');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tips = useMemo(() => buildPersonalisation(user), [user]);

  const scrollToSection = (key) => {
    setActiveSection(key);
    setTimeout(() => {
      const el = document.getElementById(`money-section-${key}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 via-white to-white">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="money-hub-page">
        {/* Back to KMS Services */}
        <button
          onClick={() => navigate('/money-hub')}
          className="mb-5 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
          data-testid="back-to-services"
        >
          ← Back to KMS Services
        </button>

        {/* Hero */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs font-medium text-emerald-700 uppercase tracking-wide mb-2">
            <PiggyBank className="w-4 h-4" /> KMS Money Hub
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
            Your money, <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">explained simply.</span>
          </h1>
          <p className="mt-3 text-base text-gray-600 max-w-2xl">
            Compare bank FD rates, Government Bonds, Post Office savings schemes, Gold investments, Mutual Funds and Retirement plans — all in one place. We never handle money. We just help you understand.
          </p>
        </div>

        {/* Strong disclaimer */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="money-hub-disclaimer">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <strong>Information only. Not financial advice.</strong> Rates change frequently — always verify on the issuer's official website before investing. KnowMySchemes does not process transactions, collect deposits, or sell financial products. We may earn no commission, no referral fees.
          </div>
        </div>

        {/* Personalisation */}
        <PersonalisationBanner tips={tips} onJump={scrollToSection} />

        {!user && (
          <div className="mb-8 bg-white border border-emerald-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="money-hub-signin-cta">
            <p className="text-sm text-gray-700">
              <Info className="w-4 h-4 inline -mt-1 mr-1 text-emerald-600" />
              <strong>Sign in</strong> to see picks personalised for your age, occupation, and family.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg"
            >
              Sign in
            </button>
          </div>
        )}

        {/* Sticky section nav */}
        <div className="sticky top-16 z-30 -mx-4 sm:mx-0 px-4 sm:px-0 bg-white/85 backdrop-blur py-3 mb-6 border-b border-gray-100">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="money-hub-tabs">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = activeSection === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => scrollToSection(s.key)}
                  data-testid={`money-tab-${s.key}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition border ${
                    isActive
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading && (
          <div className="py-16 text-center text-gray-500" data-testid="money-hub-loading">
            Loading financial products…
          </div>
        )}
        {error && (
          <div className="py-12 text-center text-rose-600" data-testid="money-hub-error">
            {error}
          </div>
        )}

        {data && (
          <div className="space-y-14">
            <section id="money-section-fd" data-testid="section-fd">
              <SectionHeader icon={PiggyBank} title="Fixed Deposits" subtitle="Bank FD rates + browser-only FD calculator." color="emerald" />
              <FDSection rows={data.fd_rates || []} />
            </section>

            <section id="money-section-bonds" data-testid="section-bonds">
              <SectionHeader icon={Landmark} title="Government Bonds" subtitle="Sovereign Gold Bonds, RBI Floating Rate, 54EC capital gains bonds." color="sky" />
              <BondsSection rows={data.govt_bonds || []} />
            </section>

            <section id="money-section-po" data-testid="section-po">
              <SectionHeader icon={Mail} title="Post Office Savings" subtitle="PPF, NSC, SCSS, Sukanya Samriddhi, KVP, MIS, MSSC, RD and more." color="amber" />
              <PostOfficeSection rows={data.post_office_schemes || []} />
            </section>

            <section id="money-section-gold" data-testid="section-gold">
              <SectionHeader icon={Coins} title="Gold Investment Options" subtitle="SGB vs ETF vs Mutual Fund vs Physical — pros & cons." color="yellow" />
              <GoldSection rows={data.gold_options || []} />
            </section>

            <section id="money-section-mf" data-testid="section-mf">
              <SectionHeader icon={LineChart} title="Mutual Fund Platforms" subtitle="Where to start a SIP in direct mutual funds." color="indigo" />
              <MFSection rows={data.mf_platforms || []} />
            </section>

            <section id="money-section-retire" data-testid="section-retire">
              <SectionHeader icon={ShieldCheck} title="Retirement Savings" subtitle="EPF, NPS and Atal Pension Yojana — secure your post-60 life." color="rose" />
              <RetirementSection rows={data.retirement_schemes || []} />
            </section>
          </div>
        )}

        {/* Bottom disclaimer */}
        <div className="mt-16 p-5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-600 leading-relaxed">
          <p className="mb-2"><strong>Sources:</strong> Reserve Bank of India · India Post · Each bank's published rate page · NHAI / REC / RBI Retail Direct portals · SEBI MF registry.</p>
          <p><strong>Last verified:</strong> Rates are updated periodically by our admins. Always reconfirm on the issuer's site before investing.</p>
        </div>
      </main>
    </div>
  );
};

function SectionHeader({ icon: Icon, title, subtitle, color }) {
  const map = {
    emerald: 'text-emerald-700',
    sky:     'text-sky-700',
    amber:   'text-amber-700',
    yellow:  'text-yellow-700',
    indigo:  'text-indigo-700',
    rose:    'text-rose-700',
  };
  return (
    <div className="mb-4 flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h2 className={`text-2xl font-bold text-gray-900 flex items-center gap-2`}>
          <Icon className={`w-6 h-6 ${map[color] || 'text-gray-700'}`} />
          {title}
        </h2>
        <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
      </div>
    </div>
  );
}

export default MoneyHub;
